"use client";

import { m as motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { Check, Minus } from "lucide-react";

/**
 * Sezione confronto "onesto" a 3 vie: Dipendente / Web agency / Abbonamento
 * Dimensione 4. Tabella su criteri (costo, partenza, flessibilita, rischio,
 * codice di proprieta) con la colonna Abbonamento evidenziata in ciano (la
 * vincente). Su mobile diventa stack di card. Le righe entrano in cascata allo
 * scroll. Solo transform/opacity, sempre con useReducedMotion. Ragionamento
 * costo onesto, nessuna metrica/social-proof inventata.
 */

interface CostComparisonProps {
  isItalian: boolean;
}

type Tone = "good" | "neutral" | "bad";

interface Cell {
  text: string;
  tone: Tone;
}

interface Row {
  criterion: string;
  employee: Cell;
  agency: Cell;
  subscription: Cell;
}

export function CostComparison({ isItalian }: CostComparisonProps) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const active = inView || reduce;

  const columns = isItalian
    ? ["Dipendente", "Web agency", "Abbonamento Dimensione 4"]
    : ["Employee", "Web agency", "Dimensione 4 subscription"];

  const rows: Row[] = isItalian
    ? [
        {
          criterion: "Costo",
          employee: {
            text: "RAL + contributi, attrezzatura, ferie e malattia, onboarding",
            tone: "bad",
          },
          agency: {
            text: "Preventivi alti, ogni modifica costa extra",
            tone: "neutral",
          },
          subscription: {
            text: "Quota fissa mensile, tutto incluso, nessuna sorpresa",
            tone: "good",
          },
        },
        {
          criterion: "Velocità di partenza",
          employee: {
            text: "Selezione e inserimento: settimane o mesi",
            tone: "bad",
          },
          agency: {
            text: "Preventivo, contratto e attesa in coda progetti",
            tone: "neutral",
          },
          subscription: {
            text: "Si parte dopo una call, lavoro sul primo task subito",
            tone: "good",
          },
        },
        {
          criterion: "Flessibilità e vincolo",
          employee: {
            text: "Contratto di lavoro, difficile da modulare",
            tone: "bad",
          },
          agency: {
            text: "Legato al singolo progetto e al suo scope",
            tone: "neutral",
          },
          subscription: {
            text: "Mensile senza vincolo, oppure trimestrale scontato",
            tone: "good",
          },
        },
        {
          criterion: "Rischio",
          employee: {
            text: "Impegno fisso anche nei mesi con poco lavoro",
            tone: "bad",
          },
          agency: {
            text: "Costo bloccato a budget, fuori scope si rinegozia",
            tone: "neutral",
          },
          subscription: {
            text: "Scali su e giù secondo il carico reale",
            tone: "good",
          },
        },
        {
          criterion: "Codice di proprietà",
          employee: { text: "Sì, ma serve gestire il team", tone: "neutral" },
          agency: {
            text: "Spesso vincolato a manutenzione e licenze",
            tone: "neutral",
          },
          subscription: {
            text: "Codice e asset tuoi al 100%, repo inclusa",
            tone: "good",
          },
        },
      ]
    : [
        {
          criterion: "Cost",
          employee: {
            text: "Salary + contributions, equipment, leave, onboarding",
            tone: "bad",
          },
          agency: {
            text: "High quotes, every change costs extra",
            tone: "neutral",
          },
          subscription: {
            text: "Fixed monthly fee, all included, no surprises",
            tone: "good",
          },
        },
        {
          criterion: "Time to start",
          employee: {
            text: "Hiring and onboarding: weeks or months",
            tone: "bad",
          },
          agency: {
            text: "Quote, contract and project queue wait",
            tone: "neutral",
          },
          subscription: {
            text: "Start after a call, first task picked up right away",
            tone: "good",
          },
        },
        {
          criterion: "Flexibility and lock-in",
          employee: {
            text: "Employment contract, hard to scale",
            tone: "bad",
          },
          agency: {
            text: "Tied to a single project and its scope",
            tone: "neutral",
          },
          subscription: {
            text: "Monthly with no lock-in, or a discounted quarterly",
            tone: "good",
          },
        },
        {
          criterion: "Risk",
          employee: {
            text: "Fixed commitment even in slow months",
            tone: "bad",
          },
          agency: {
            text: "Budget locked, out-of-scope gets renegotiated",
            tone: "neutral",
          },
          subscription: {
            text: "Scale up and down with the real workload",
            tone: "good",
          },
        },
        {
          criterion: "Code ownership",
          employee: { text: "Yes, but you manage the team", tone: "neutral" },
          agency: {
            text: "Often tied to maintenance and licenses",
            tone: "neutral",
          },
          subscription: {
            text: "Code and assets 100% yours, repo included",
            tone: "good",
          },
        },
      ];

  const toneText = (tone: Tone, highlight: boolean) => {
    if (highlight) return "text-foreground/90";
    if (tone === "bad") return "text-muted-foreground/70";
    return "text-muted-foreground";
  };

  const Icon = ({ tone, highlight }: { tone: Tone; highlight: boolean }) =>
    tone === "good" || highlight ? (
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
    ) : (
      <Minus className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40" />
    );

  return (
    <section ref={ref} className="py-20 md:py-28 lg:py-28">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={active ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12 text-left"
        >
          <span className="font-mono text-label text-primary mb-4 block">
            {isItalian ? "Il confronto onesto" : "The honest comparison"}
          </span>
          <h2 className="font-display text-h2 font-bold mb-4">
            {isItalian ? (
              <>
                Dipendente, agenzia o{" "}
                <span className="text-primary">abbonamento?</span>
              </>
            ) : (
              <>
                Employee, agency or{" "}
                <span className="text-primary">subscription?</span>
              </>
            )}
          </h2>
          <p className="text-body-lg text-muted-foreground max-w-4xl">
            {isItalian
              ? "Uno sviluppatore continuativo, senza i costi e i vincoli di un'assunzione né i tempi di un'agenzia. Un consulente senior in Italia costa da 280 a oltre 500 € al giorno: l'abbonamento ti dà continuità a quota fissa."
              : "A continuous developer, without the costs and lock-in of a hire or the timelines of an agency. A senior consultant in Italy costs from 280 to over 500 € per day: the subscription gives you continuity at a flat fee."}
          </p>
        </motion.div>

        {/* Desktop / tablet: tabella (scroll orizzontale se serve) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={active ? { opacity: 1, y: 0 } : {}}
          transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.1 }}
          className="hidden md:block"
        >
          <div className="overflow-x-auto rounded-2xl border border-[hsl(var(--border))] bg-surface">
            <table className="w-full min-w-[680px] border-collapse text-left">
              <thead>
                <tr className="border-b border-[hsl(var(--border))]">
                  <th className="w-[18%] border-r border-[hsl(var(--border))] p-4" />
                  {columns.map((col, i) => {
                    const highlight = i === 2;
                    return (
                      <th
                        key={col}
                        className={`p-4 align-bottom ${
                          i < 2 ? "border-r border-[hsl(var(--border))]" : ""
                        } ${highlight ? "relative bg-primary/[0.06]" : ""}`}
                      >
                        {highlight && (
                          <span className="absolute inset-x-0 top-0 h-[3px] rounded-b bg-gradient-to-r from-primary/40 via-primary to-primary/40" />
                        )}
                        <span
                          className={`font-display text-[0.95rem] font-semibold whitespace-nowrap ${
                            highlight ? "text-primary" : "text-foreground/85"
                          }`}
                        >
                          {col}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <motion.tr
                    key={row.criterion}
                    initial={reduce ? false : { opacity: 0, y: 12 }}
                    animate={active ? { opacity: 1, y: 0 } : {}}
                    transition={
                      reduce
                        ? { duration: 0 }
                        : { duration: 0.4, delay: 0.18 + ri * 0.08 }
                    }
                    className="border-b border-[hsl(var(--border))] last:border-b-0"
                  >
                    <th
                      scope="row"
                      className="border-r border-[hsl(var(--border))] p-4 align-top font-mono text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-foreground/80"
                    >
                      {row.criterion}
                    </th>
                    {(
                      [row.employee, row.agency, row.subscription] as const
                    ).map((cell, ci) => {
                      const highlight = ci === 2;
                      return (
                        <td
                          key={ci}
                          className={`p-4 align-top text-[0.8rem] leading-relaxed ${
                            ci < 2 ? "border-r border-[hsl(var(--border))]" : ""
                          } ${highlight ? "bg-primary/[0.06]" : ""} ${toneText(
                            cell.tone,
                            highlight
                          )}`}
                        >
                          <span className="flex items-start gap-2">
                            <Icon tone={cell.tone} highlight={highlight} />
                            <span>{cell.text}</span>
                          </span>
                        </td>
                      );
                    })}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Mobile: stack di card, abbonamento in evidenza per primo */}
        <div className="space-y-4 md:hidden">
          {[2, 0, 1].map((colIndex, order) => {
            const highlight = colIndex === 2;
            return (
              <motion.div
                key={columns[colIndex]}
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={active ? { opacity: 1, y: 0 } : {}}
                transition={
                  reduce
                    ? { duration: 0 }
                    : { duration: 0.45, delay: 0.1 + order * 0.1 }
                }
                className={`rounded-2xl border p-5 ${
                  highlight
                    ? "border-primary/40 bg-primary/[0.06]"
                    : "border-[hsl(var(--border))] bg-surface"
                }`}
              >
                <h3
                  className={`mb-4 font-display text-lg font-semibold ${
                    highlight ? "text-primary" : "text-foreground/85"
                  }`}
                >
                  {columns[colIndex]}
                </h3>
                <ul className="space-y-3">
                  {rows.map((row) => {
                    const cell = [
                      row.employee,
                      row.agency,
                      row.subscription,
                    ][colIndex];
                    return (
                      <li key={row.criterion} className="flex gap-3">
                        <Icon tone={cell.tone} highlight={highlight} />
                        <div className="min-w-0">
                          <span className="block font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground/75">
                            {row.criterion}
                          </span>
                          <span
                            className={`text-sm leading-relaxed ${toneText(
                              cell.tone,
                              highlight
                            )}`}
                          >
                            {cell.text}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
